/**
 * FeedbackModal Component
 *
 * Modal dialog for submitting user feedback.
 * Supports bug reports, feature requests, and general feedback.
 */

import { useState, useCallback, type CSSProperties } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getFirebaseDb, getFirebaseStorage } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { ImageDropZone, MIME_TO_EXTENSION } from '@/components/common/ImageDropZone';
import type { FeedbackType, FeedbackPriority } from '@flash/shared';

/**
 * Props for FeedbackModal component
 */
interface FeedbackModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when modal is closed */
  onClose: () => void;
  /** Called after successful submission */
  onSuccess?: () => void;
}

/**
 * FeedbackModal component for collecting user feedback
 */
export const FeedbackModal = ({ isOpen, onClose, onSuccess }: FeedbackModalProps) => {
  const { user } = useAuth();

  // Form state
  const [description, setDescription] = useState('');
  const [feedbackType, setFeedbackType] = useState<FeedbackType | ''>('');
  const [priority, setPriority] = useState<FeedbackPriority | ''>('');
  const [canFollowUp, setCanFollowUp] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setDescription('');
    setFeedbackType('');
    setPriority('');
    setCanFollowUp(false);
    setScreenshot(null);
    setScreenshotPreview(null);
    setError(null);
    setScreenshotError(null);
    setSuccess(false);
    setUploadProgress(0);
  }, []);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  }, [isSubmitting, resetForm, onClose]);

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  /**
   * Handle screenshot file selection from ImageDropZone
   */
  const handleScreenshotSelect = useCallback((file: File) => {
    setScreenshotError(null);
    setScreenshot(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result as string);
    };
    reader.onerror = () => {
      setScreenshotError('Failed to read image file');
      setScreenshot(null);
      setScreenshotPreview(null);
    };
    reader.readAsDataURL(file);
  }, []);

  /**
   * Remove selected screenshot
   */
  const handleRemoveScreenshot = useCallback(() => {
    setScreenshot(null);
    setScreenshotPreview(null);
    setScreenshotError(null);
  }, []);

  /**
   * Upload screenshot to Firebase Storage
   */
  const uploadScreenshot = async (file: File): Promise<string> => {
    const storage = getFirebaseStorage();
    const timestamp = Date.now();
    // Use MIME type to derive extension instead of trusting filename
    // This prevents path traversal attacks via malicious filenames
    const extension = MIME_TO_EXTENSION[file.type] ?? 'png';
    const storagePath = `feedback/${user?.uid}/${timestamp}.${extension}`;
    const storageRef = ref(storage, storagePath);

    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          uploadedBy: user?.uid ?? 'unknown',
          originalName: file.name,
        },
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress(progress);
        },
        (uploadError) => {
          reject(new Error(`Screenshot upload failed: ${uploadError.message}`));
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch {
            reject(new Error('Failed to get screenshot URL'));
          }
        }
      );
    });
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!user) {
      setError('You must be logged in to submit feedback');
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Upload screenshot if provided
      let screenshotUrl: string | null = null;
      if (screenshot) {
        screenshotUrl = await uploadScreenshot(screenshot);
      }

      // Create feedback document
      const db = getFirebaseDb();
      const feedbackRef = collection(db, 'feedback');

      await addDoc(feedbackRef, {
        description: description.trim(),
        type: feedbackType || null,
        priority: priority || null,
        screenshotUrl,
        canFollowUp,

        // Auto-captured fields
        userId: user.uid,
        userEmail: user.email ?? '',
        userDisplayName: user.displayName ?? '',
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,

        // Metadata
        status: 'new',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),

        // Admin fields (null by default)
        adminNotes: null,
        resolvedAt: null,
        resolvedBy: null,
      });

      setSuccess(true);

      // Call success callback after a brief delay to show success message
      setTimeout(() => {
        resetForm();
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (submitError) {
      const errorMessage =
        submitError instanceof Error ? submitError.message : 'Failed to submit feedback';
      console.error('Error submitting feedback:', submitError);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={overlayStyle}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 id="feedback-modal-title" style={titleStyle}>
            Send Feedback
          </h2>
          <button
            type="button"
            onClick={handleClose}
            style={closeButtonStyle}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Success Message */}
        {success ? (
          <div style={successContainerStyle}>
            <div style={successIconStyle}>
              <CheckCircleOutlineIcon style={{ fontSize: 48, color: 'var(--color-success)' }} />
            </div>
            <h3 style={successTitleStyle}>Thank you!</h3>
            <p style={successMessageStyle}>Your feedback has been submitted.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Content */}
            <div style={contentStyle}>
              {/* Description (required) */}
              <div style={fieldStyle}>
                <label htmlFor="feedback-description" style={labelStyle}>
                  Description <span style={requiredStyle}>*</span>
                </label>
                <textarea
                  id="feedback-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us what's on your mind..."
                  style={textareaStyle}
                  rows={4}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Feedback Type (optional) */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Type (optional)</label>
                <div style={radioGroupStyle}>
                  {(['bug', 'feature', 'general'] as const).map((type) => (
                    <label key={type} style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="feedbackType"
                        value={type}
                        checked={feedbackType === type}
                        onChange={() => setFeedbackType(type)}
                        style={radioInputStyle}
                        disabled={isSubmitting}
                      />
                      <span style={radioTextStyle}>
                        {type === 'bug'
                          ? 'Bug Report'
                          : type === 'feature'
                            ? 'Feature Request'
                            : 'General'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Priority (optional) */}
              <div style={fieldStyle}>
                <label htmlFor="feedback-priority" style={labelStyle}>
                  Priority (optional)
                </label>
                <select
                  id="feedback-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as FeedbackPriority | '')}
                  style={selectStyle}
                  disabled={isSubmitting}
                >
                  <option value="">Select priority...</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Screenshot (optional) */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Screenshot (optional)</label>
                <ImageDropZone
                  onFileSelect={handleScreenshotSelect}
                  onRemove={handleRemoveScreenshot}
                  previewUrl={screenshotPreview}
                  disabled={isSubmitting}
                  error={screenshotError}
                  onError={setScreenshotError}
                  placeholder="Drag & drop or click to add screenshot"
                  dragActiveText="Drop screenshot here"
                />
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div style={progressBarContainerStyle}>
                    <div style={{ ...progressBarStyle, width: `${uploadProgress}%` }} />
                  </div>
                )}
              </div>

              {/* Contact Preference (optional) */}
              <div style={fieldStyle}>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={canFollowUp}
                    onChange={(e) => setCanFollowUp(e.target.checked)}
                    style={checkboxInputStyle}
                    disabled={isSubmitting}
                  />
                  <span style={checkboxTextStyle}>
                    You can follow up with me about this feedback
                  </span>
                </label>
              </div>

              {/* Error Message */}
              {error && <div style={errorStyle}>{error}</div>}
            </div>

            {/* Actions */}
            <div style={actionsStyle}>
              <button
                type="button"
                onClick={handleClose}
                style={cancelButtonStyle}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...submitButtonStyle,
                  ...(isSubmitting ? disabledButtonStyle : {}),
                }}
                disabled={isSubmitting || !description.trim()}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/**
 * Styles
 */
const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
  zIndex: 1000,
};

const modalStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  maxWidth: '500px',
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: 'var(--shadow-lg)',
};

const headerStyle: CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--color-border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const titleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const closeButtonStyle: CSSProperties = {
  padding: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '6px',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentStyle: CSSProperties = {
  padding: '20px 24px',
};

const fieldStyle: CSSProperties = {
  marginBottom: '20px',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const requiredStyle: CSSProperties = {
  color: 'var(--color-error)',
};

const textareaStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '0.9375rem',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  resize: 'vertical',
  minHeight: '100px',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

const radioGroupStyle: CSSProperties = {
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
};

const radioLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
};

const radioInputStyle: CSSProperties = {
  flexShrink: 0,
};

const radioTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
};

const selectStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '0.9375rem',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

const progressBarContainerStyle: CSSProperties = {
  marginTop: '8px',
  height: '4px',
  backgroundColor: 'var(--color-bg-tertiary)',
  borderRadius: '2px',
  overflow: 'hidden',
};

const progressBarStyle: CSSProperties = {
  height: '100%',
  backgroundColor: 'var(--color-primary)',
  transition: 'width 0.2s',
};

const checkboxLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  cursor: 'pointer',
};

const checkboxInputStyle: CSSProperties = {
  flexShrink: 0,
  marginTop: '2px',
};

const checkboxTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  lineHeight: 1.5,
};

const errorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-error)',
  fontSize: '0.875rem',
};

const actionsStyle: CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid var(--color-border)',
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  cursor: 'pointer',
};

const submitButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
};

const successContainerStyle: CSSProperties = {
  padding: '40px 24px',
  textAlign: 'center',
};

const successIconStyle: CSSProperties = {
  marginBottom: '16px',
};

const successTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const successMessageStyle: CSSProperties = {
  fontSize: '0.9375rem',
  color: 'var(--color-text-secondary)',
};

export default FeedbackModal;
